import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Box, Chip, FormControlLabel, ListItemText, MenuItem, Stack, Switch,
  TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import FormDrawer from '../../components/ui/FormDrawer';
import api from '../../services/api';
import {
  defaultSampleVars,
  interpolatePushTemplate,
  type PushTemplateRow,
} from '../../utils/pushTemplatePreview';
import {
  extractPlaceholders,
  sanitizeTemplateName,
  validateTemplateForm,
  type NotificationTemplateReference,
  type TemplateFieldErrors,
  type TemplateFormState,
} from '../../utils/notificationTemplateValidation';

const EMPTY_FORM: TemplateFormState = {
  name: '',
  code: '',
  channel: '',
  subject: '',
  body: '',
  isActive: false,
};

type Field = keyof TemplateFieldErrors;
const FIELD_ORDER: Field[] = ['name', 'code', 'channel', 'subject', 'body'];
const FIELD_LABEL: Record<Field, string> = {
  name: 'Name',
  code: 'Event code',
  channel: 'Channel',
  subject: 'Title',
  body: 'Body',
};

function ChannelPreview({ channel, title, body }: { channel: string; title: string; body: string }) {
  const theme = useTheme();
  const placeholder = (text: string, hint: string) => text || (
    <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>{hint}</Box>
  );

  if (channel === 'sms' || channel === 'whatsapp') {
    return (
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.03), border: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={650}>
          {channel === 'sms' ? 'SMS message' : 'WhatsApp message'}
        </Typography>
        <Box
          sx={{
            mt: 0.75,
            maxWidth: '85%',
            px: 1.5,
            py: 1,
            borderRadius: 2,
            borderTopLeftRadius: 4,
            bgcolor: channel === 'whatsapp' ? alpha('#25D366', 0.14) : 'background.paper',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {placeholder(body, 'Your message appears here')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (channel === 'email') {
    return (
      <Box sx={{ borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1, bgcolor: alpha(theme.palette.text.primary, 0.03), borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">Subject</Typography>
          <Typography variant="body2" fontWeight={700} noWrap>{placeholder(title, 'Email subject')}</Typography>
        </Box>
        <Typography variant="body2" sx={{ p: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {placeholder(body, 'Email body appears here')}
        </Typography>
      </Box>
    );
  }

  const isPush = channel === 'push';
  return (
    <Box sx={{ borderRadius: 2.5, p: 1.75, bgcolor: alpha(theme.palette.primary.main, 0.05), border: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 36, height: 36, borderRadius: 1.75, bgcolor: 'primary.main', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0,
          }}
        >
          {isPush ? 'App' : 'Inbox'}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography fontWeight={700} noWrap sx={{ fontSize: '0.875rem' }}>
            {placeholder(title, 'Notification title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {placeholder(body, 'Notification body')}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function NotificationTemplateDrawer({
  open, editing, onClose,
}: {
  open: boolean;
  editing: PushTemplateRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<TemplateFormState>(EMPTY_FORM);
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});
  const [focusField, setFocusField] = useState<'subject' | 'body'>('body');
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const { data: reference } = useQuery({
    queryKey: ['notification-template-reference'],
    queryFn: async () => (await api.get('/notifications/admin/templates/reference')).data.data as NotificationTemplateReference,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setTouched({});
    setFocusField('body');
    setForm(editing ? {
      name: editing.name || '',
      code: editing.code || '',
      channel: editing.channel || '',
      subject: editing.subject || '',
      body: editing.body || '',
      isActive: editing.isActive === true,
    } : EMPTY_FORM);
  }, [open, editing]);

  const event = reference?.events.find((e) => e.code === form.code);
  const rule = reference?.channels.find((c) => c.value === form.channel);
  const needsTitle = rule ? rule.title === 'required' : true;

  const takenNames = useMemo(
    () => (reference?.names || []).filter((n) => n.id !== editing?._id).map((n) => n.name.trim().toLowerCase()),
    [reference, editing],
  );
  const errors = useMemo(
    () => validateTemplateForm(form, reference, { editing: !!editing, takenNames }),
    [form, reference, editing, takenNames],
  );
  const errorFields = FIELD_ORDER.filter((f) => errors[f] && (f !== 'subject' || needsTitle));
  const valid = !!reference && errorFields.length === 0;
  const showError = (f: Field) => (touched[f] ? errors[f] : undefined);

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        code: form.code,
        channel: form.channel,
        subject: needsTitle ? form.subject.trim() : '',
        body: form.body.trim(),
        isActive: form.isActive && valid,
      };
      if (editing?._id) return api.put(`/notifications/admin/templates/${editing._id}`, payload);
      return api.post('/notifications/admin/templates', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
      queryClient.invalidateQueries({ queryKey: ['notification-template-reference'] });
      onClose();
    },
  });
  const serverError = saveMut.isError
    ? ((saveMut.error as { response?: { data?: { message?: string } } })?.response?.data?.message
      || (saveMut.error as Error)?.message || 'Could not save template')
    : '';

  useEffect(() => { if (open) saveMut.reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<TemplateFormState>) => setForm((prev) => {
    const next = { ...prev, ...patch };
    if (next.isActive && Object.keys(validateTemplateForm(next, reference, { editing: !!editing, takenNames })).length) {
      next.isActive = false;
    }
    return next;
  });

  const suggestName = (code: string, channel: string) => {
    const ev = reference?.events.find((e) => e.code === code);
    const ch = reference?.channels.find((c) => c.value === channel);
    if (!ev) return;
    const base = ch && ch.value !== 'push' ? `${ev.label} (${ch.label})` : ev.label;
    if (!takenNames.includes(base.toLowerCase())) set({ name: sanitizeTemplateName(base), code, channel });
  };

  const insertVariable = (variable: string) => {
    const token = `{{${variable}}}`;
    const field = needsTitle ? focusField : 'body';
    const el = field === 'subject' ? subjectRef.current : bodyRef.current;
    const current = form[field];
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    set({ [field]: next } as Partial<TemplateFormState>);
    setTouched((t) => ({ ...t, [field]: true }));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const sampleVars = defaultSampleVars(form.code, event?.variables || []);
  const previewTitle = interpolatePushTemplate(form.subject, sampleVars);
  const previewBody = interpolatePushTemplate(form.body, sampleVars);
  const usedVars = extractPlaceholders(`${needsTitle ? form.subject : ''} ${form.body}`);
  const isLive = !!event && !!form.channel && event.liveChannels.includes(form.channel);

  const submitHint = !reference
    ? 'Loading events…'
    : errorFields.length
      ? `${editing ? 'Save' : 'Create'} is blocked — fix ${errorFields.map((f) => FIELD_LABEL[f] === 'Title' && rule ? rule.titleLabel : FIELD_LABEL[f]).join(', ')}`
      : undefined;

  const counter = (len: number, max?: number) => (max ? `${len}/${max}` : '');

  return (
    <FormDrawer
      open={open}
      title={editing ? 'Edit template' : 'New template'}
      onClose={onClose}
      onSubmit={() => {
        setTouched({ name: true, code: true, channel: true, subject: true, body: true });
        if (valid) saveMut.mutate();
      }}
      submitLabel={editing ? 'Save' : 'Create'}
      submitHint={submitHint}
      loading={saveMut.isPending}
      disableSubmit={!valid}
      width={600}
    >
      <Stack spacing={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}

        <Autocomplete
          options={reference?.events || []}
          value={event || null}
          disabled={!!editing}
          loading={!reference}
          onChange={(_, ev) => {
            setTouched((t) => ({ ...t, code: true }));
            const code = ev?.code || '';
            const channel = form.channel && ev?.existingChannels.includes(form.channel) && !editing ? '' : form.channel;
            if (!form.name.trim() && code && channel) suggestName(code, channel);
            else set({ code, channel });
          }}
          getOptionLabel={(o) => `${o.label} — ${o.code}`}
          isOptionEqualToValue={(a, b) => a.code === b.code}
          renderOption={(props, o) => (
            <li {...props} key={o.code}>
              <ListItemText
                primary={o.label}
                secondary={`${o.code} · ${o.liveChannels.length ? `sent by the app on ${o.liveChannels.join(', ')}` : 'not sent by the app'}`}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
              />
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Event code"
              placeholder="Search events the app sends"
              error={!!showError('code')}
              helperText={showError('code') || (editing
                ? 'Event code is locked after creation — create a new template for a different event'
                : 'Only events the app actually fires are listed, so the template is always mapped to a runtime event')}
            />
          )}
        />

        <TextField
          select
          required
          fullWidth
          label="Channel"
          value={form.channel}
          disabled={!!editing}
          onChange={(e) => {
            setTouched((t) => ({ ...t, channel: true }));
            if (!form.name.trim() && form.code) suggestName(form.code, e.target.value);
            else set({ channel: e.target.value });
          }}
          error={!!showError('channel')}
          helperText={showError('channel') || (form.channel
            ? (isLive ? 'The app sends this event on this channel' : 'Saved for reference — the app does not send this event on this channel yet')
            : 'One template per event per channel')}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="" disabled><em>Select channel</em></MenuItem>
          {(reference?.channels || []).map((c) => {
            const exists = !editing && !!event?.existingChannels.includes(c.value);
            const live = !!event?.liveChannels.includes(c.value);
            return (
              <MenuItem key={c.value} value={c.value} disabled={exists}>
                <ListItemText
                  primary={c.label}
                  secondary={exists ? 'Already exists for this event' : event ? (live ? 'Sent by the app' : 'Not sent by the app') : undefined}
                />
              </MenuItem>
            );
          })}
        </TextField>

        <TextField
          required
          fullWidth
          label="Template name"
          value={form.name}
          onChange={(e) => set({ name: sanitizeTemplateName(e.target.value) })}
          onBlur={() => setTouched((t) => ({ ...t, name: true }))}
          error={!!showError('name')}
          helperText={showError('name') || 'Unique, 3–60 characters, e.g. "Order Confirmed"'}
          inputProps={{ maxLength: 60 }}
        />

        {needsTitle ? (
          <TextField
            required
            fullWidth
            label={rule?.titleLabel || 'Title'}
            value={form.subject}
            inputRef={subjectRef}
            onFocus={() => setFocusField('subject')}
            onChange={(e) => set({ subject: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, subject: true }))}
            error={!!showError('subject')}
            helperText={
              <Stack direction="row" justifyContent="space-between" component="span">
                <span>{showError('subject') || `Shown as the ${rule?.label || ''} ${(rule?.titleLabel || 'title').toLowerCase()}`}</span>
                <span>{counter(form.subject.length, rule?.titleMax)}</span>
              </Stack>
            }
          />
        ) : (
          <Alert severity="info" sx={{ py: 0.25 }}>{rule?.label} messages have no title — only the body is sent.</Alert>
        )}

        <TextField
          required
          fullWidth
          multiline
          minRows={4}
          label="Body"
          value={form.body}
          inputRef={bodyRef}
          onFocus={() => setFocusField('body')}
          onChange={(e) => set({ body: e.target.value })}
          onBlur={() => setTouched((t) => ({ ...t, body: true }))}
          error={!!showError('body')}
          helperText={
            <Stack direction="row" justifyContent="space-between" component="span">
              <span>{showError('body') || 'Use {{variable}} for dynamic values — pick from the list below'}</span>
              <span>{counter(form.body.length, rule?.bodyMax)}</span>
            </Stack>
          }
        />

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 650 }}>
            Variables {event ? `for ${event.code}` : ''} — click to insert into the {needsTitle && focusField === 'subject' ? (rule?.titleLabel || 'title').toLowerCase() : 'body'}
          </Typography>
          {event ? (
            event.variables.length ? (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {event.variables.map((v) => (
                  <Tooltip key={v} title={`Sample: ${sampleVars[v] ?? ''}`}>
                    <Chip
                      size="small"
                      label={`{{${v}}}`}
                      clickable
                      color={usedVars.includes(v) ? 'primary' : 'default'}
                      variant={usedVars.includes(v) ? 'filled' : 'outlined'}
                      onClick={() => insertVariable(v)}
                      sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">This event sends no variables — use plain text.</Typography>
            )
          ) : (
            <Typography variant="body2" color="text.disabled">Pick an event code to see its variables.</Typography>
          )}
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                disabled={!valid}
                onChange={(e) => set({ isActive: e.target.checked })}
              />
            }
            label="Active"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
            {valid
              ? (isLive ? 'When active, the app uses this template instead of its built-in text.' : 'Active, but this channel is not sent by the app yet.')
              : 'Complete all required fields before activating.'}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 650 }}>
            Preview {rule ? `· ${rule.label}` : ''} (sample values)
          </Typography>
          {form.channel ? (
            <ChannelPreview channel={form.channel} title={previewTitle} body={previewBody} />
          ) : (
            <Typography variant="body2" color="text.disabled">Select a channel to preview.</Typography>
          )}
        </Box>

        <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">Checklist</Typography>
          <Stack spacing={0.5} sx={{ mt: 0.75 }}>
            {FIELD_ORDER.filter((f) => f !== 'subject' || needsTitle).map((f) => {
              const ok = !errors[f] && !!reference && (f !== 'subject' || !!rule);
              return (
                <Stack key={f} direction="row" spacing={0.75} alignItems="flex-start">
                  {ok
                    ? <CheckCircle sx={{ fontSize: 16, color: 'success.main', mt: '2px' }} />
                    : <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.disabled', mt: '2px' }} />}
                  <Typography variant="body2" color={ok ? 'text.primary' : 'text.secondary'}>
                    <strong>{f === 'subject' ? rule?.titleLabel || 'Title' : FIELD_LABEL[f]}</strong>
                    {!ok && errors[f] ? ` — ${errors[f]}` : ''}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </FormDrawer>
  );
}
